// EarningsDashboard.
//
// Restructured Feb 20, 2026 (per user audit). New order:
//   1. Action strip — LLM credit + Top up + Stripe Connect setup (top of mind)
//   2. Operator OS — Churn / Retention / Tax CSV (business health)
//   3. Affiliate payouts — where the active money actually flows
//   4. Silo earnings — per-silo + sparkline (with empty state) + Tips
//
// Killed: the 4-card hero strip (all stats duplicated content from below),
// the wrapper "Tips from your fans" header (TipsModule has its own).

import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  CurrencyDollar, Users, Clock, Copy, Share, Plus, TrendUp, ArrowRight,
  Wallet, Bank, Heart, Crown, Stack,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { usePublicPricing, fmtPct } from "@/lib/usePublicPricing";
import TipsModule from "@/components/TipsModule";
import InfoTip from "@/components/InfoTip";
import GhostReport from "@/components/GhostReport";
import AffiliateUpgrade from "@/components/AffiliateUpgrade";
import AffiliatePayoutsLedger from "@/components/AffiliatePayoutsLedger";
import EarnedThisMonthCard from "@/components/EarnedThisMonthCard";
import WithdrawModal from "@/components/WithdrawModal";
import VoucherTopupModal from "@/components/VoucherTopupModal";
import OperatorOSPanel from "@/components/OperatorOSPanel";
import { OperatorDfyQueue, OperatorOptInToggle } from "@/components/DfyQueue";

const fmt$ = (cents) => `$${((cents || 0) / 100).toFixed(((cents || 0) % 100) === 0 ? 0 : 2)}`;

export default function EarningsDashboard({ onTopUp }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [chartView, setChartView] = useState("total"); // 'total' | 'tips' | 'subs'
  const [shareLink, setShareLink] = useState(null); // { referral_code, share_url }
  const [me, setMe] = useState(null);
  const [silos, setSilos] = useState([]);
  const [stripeCfg, setStripeCfg] = useState(null); // /stripe/config — drives cashout button state
  const [stripeBalance, setStripeBalance] = useState(null); // Feb 21, 2026 — live Stripe Express balance
  // Feb 21, 2026 (late eve) — last 5 Express payouts pulled from Stripe.
  // Mirrors the SuperAdmin platform-payouts widget so each creator can
  // see at-a-glance which payouts actually swept to their bank.
  const [stripePayouts, setStripePayouts] = useState(null);
  const [cashoutOpen, setCashoutOpen] = useState(false);
  const [voucherTopupOpen, setVoucherTopupOpen] = useState(false);
  const [connectBusy, setConnectBusy] = useState(false);
  const pricing = usePublicPricing();
  // Oct 3 2026 — these used to fall back to 25/30. The live performance rate
  // is 33, so the remembered 30 was already telling partners the wrong rate
  // whenever /public/pricing was slow. An unknown rate renders "—".
  const basePct = fmtPct(pricing.affiliate_base_rev_share_pct);
  const perfPct = fmtPct(pricing.affiliate_performance_rev_share_pct);

  const reload = async () => {
    try {
      const [earningsRes, meRes] = await Promise.all([
        api.get("/me/earnings"),
        api.get("/auth/me").catch(() => null),
      ]);
      setData(earningsRes.data);
      if (meRes?.data) setMe(meRes.data);
    } catch { /* swallow — stale data better than blank */ }
  };

  useEffect(() => {
    let cancel = false;
    // May 25, 2026 — Retry once on transient failure (matches the
    // Dashboard pattern). Hot-reload races would otherwise show a stale
    // "Failed to load earnings" toast every refresh during dev.
    const attempt = async () => {
      const [earningsRes, linkRes, meRes, silosRes, cfgRes, balRes, payoutsRes] = await Promise.all([
        api.get("/me/earnings"),
        api.get("/me/referral-link").catch(() => null),
        api.get("/auth/me").catch(() => null),
        api.get("/silos").catch(() => ({ data: [] })),
        api.get("/stripe/config").catch(() => null),
        api.get("/stripe/connect/balance").catch(() => null),
        api.get("/stripe/connect/payouts?limit=5").catch(() => null),
      ]);
      if (cancel) return;
      setData(earningsRes.data);
      if (linkRes?.data) setShareLink(linkRes.data);
      if (meRes?.data) setMe(meRes.data);
      setSilos(silosRes?.data || []);
      if (cfgRes?.data) setStripeCfg(cfgRes.data);
      if (balRes?.data) setStripeBalance(balRes.data);
      if (payoutsRes?.data?.ok) setStripePayouts(payoutsRes.data.payouts || []);
    };
    (async () => {
      try {
        await attempt();
      } catch (e) {
        const isTransient = !e.response || (e.response.status >= 500 && e.response.status < 600);
        if (isTransient) {
          await new Promise((r) => setTimeout(r, 1500));
          try { await attempt(); return; } catch { /* fall through */ }
        }
        if (!cancel) toast.error(e.response?.data?.detail || "Failed to load earnings.");
      } finally { if (!cancel) setLoading(false); }
    })();
    return () => { cancel = true; };
  }, []);

  // Stripe Connect onboarding kick-off. Used in both the "not connected"
  // and "onboarding pending" states — Stripe's hosted flow resumes whichever
  // step the user is on.
  const startStripeOnboarding = async () => {
    setConnectBusy(true);
    try {
      const r = await api.post("/stripe/connect/start", { origin_url: window.location.origin });
      if (r.data?.url) {
        window.location.href = r.data.url;
      } else {
        throw new Error("No onboarding URL returned");
      }
    } catch (e) {
      setConnectBusy(false);
      toast.error(e.response?.data?.detail || "Couldn't open Stripe onboarding.");
    }
  };

  // Note: Stripe Express dashboard is opened through WithdrawModal (which
  // pre-fetches the URL and renders a real <a target="_blank">), not from
  // this component directly. The old `openStripeDashboard` JS helper was
  // removed Feb 21, 2026 (late eve) because it tripped iOS Safari's
  // popup-blocker and was nowhere referenced.

  if (loading) return <div className="text-[#6B6A68] text-sm py-4" data-testid="earnings-loading">Loading earnings…</div>;
  if (!data) return null;

  // Phase-1: prefer the short referral_code; fall back to legacy UUID referrer_sig.
  // Sprint Feb 8, 2026: silo-anchored share URL (no more naked /?ref=).
  const inviteCode = shareLink?.referral_code || data.referrals?.referrer_sig || "";
  const primarySilo = silos[0];
  const inviteUrl = (inviteCode && primarySilo)
    ? `${window.location.origin}/s/${primarySilo.slug}?ref=${encodeURIComponent(inviteCode)}`
    : "";
  const canShare = !!inviteUrl;
  const copyInvite = async () => {
    if (!canShare) return;
    try { await navigator.clipboard.writeText(inviteUrl); toast.success("Invite link copied."); }
    catch { toast.error("Copy failed."); }
  };

  const series = data.time_series || [];
  const max = Math.max(1, ...series.map((m) => m[`${chartView}_cents`] || 0));

  return (
    <div data-testid="earnings-dashboard">
      <div className="mb-6">
        <h2 className="font-serif-display text-2xl text-[#1C1B1A]">Your earnings.</h2>
        <p className="text-sm text-[#6B6A68] mt-1 max-w-2xl">
          {me?.is_affiliate
            ? "Two streams in one place: your own silo revenue, and the Partner payouts you've earned from creators you've referred."
            : "Tips and subscription revenue from your own silos, all in one place."}
        </p>
      </div>

      {/* ============================================================
          SECTION 1 — ACTION STRIP (Withdraw earnings + Stripe Connect)
          Feb 20, 2026: LLM credit balance row REMOVED — that concept
          retired in favor of per-silo platform-key vouchers (see silo
          Ops). This strip is now purely about EARNINGS withdrawal.
          ============================================================ */}
      <section className="mb-8 bg-gradient-to-br from-[#FDFCF8] to-[#F0F1EE] border-2 border-dashed hairline rounded-xl p-6" data-testid="earnings-cashout-section">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-md bg-white border hairline flex items-center justify-center">
              <Bank size={22} weight="duotone" className="text-[#4A5D4E]" />
            </div>
            <div>
              <div className="text-base font-medium text-[#1C1B1A]">Withdraw earnings</div>
              <div className="text-xs text-[#6B6A68] max-w-xl">
                Your creator earnings (fan subs + tips) and Partner Program rev-share auto-route to your Stripe Express account.
                Withdraw to your bank from there — we don't hold your money.
              </div>
              {/* Feb 21, 2026 — live Stripe Express balance display. Pulled
                  via stripe.Balance.retrieve(stripe_account=acct_xxx). The
                  "available" amount is cashable to bank immediately;
                  "pending" is funds still settling in Stripe's 2-day window. */}
              {stripeBalance && stripeBalance.connected && (
                <div className="mt-2 flex items-center gap-3 text-xs flex-wrap" data-testid="earnings-stripe-balance">
                  <span className="px-2 py-1 rounded-md bg-[#4A5D4E]/10 text-[#4A5D4E] font-medium tabular-nums"
                    data-testid="earnings-stripe-available">
                    {fmt$(stripeBalance.available_cents)} available
                  </span>
                  {stripeBalance.pending_cents > 0 && (
                    <span className="px-2 py-1 rounded-md bg-[#C16E5A]/10 text-[#C16E5A] tabular-nums"
                      data-testid="earnings-stripe-pending"
                      title="Funds still settling in Stripe (typically 2 business days for new accounts; in test mode this never advances)">
                      {fmt$(stripeBalance.pending_cents)} settling
                    </span>
                  )}
                  <span className="text-[10px] text-[#6B6A68]">Live from Stripe</span>
                </div>
              )}
              {/* Feb 21, 2026 — plain-English breakdown of where the
                  settling amount comes from. Helps users connect the
                  per-silo earnings cards to the top-line Stripe balance. */}
              {stripeBalance && stripeBalance.connected && stripeBalance.pending_cents > 0 && (
                <div className="mt-1.5 text-[10px] text-[#6B6A68] leading-snug max-w-xl" data-testid="earnings-settling-explainer">
                  Settling = money Stripe has, charging now but not yet released to your bank. Includes fan-sub payments
                  and any rev-share that already cleared. Live mode releases ~2 business days after each charge;
                  test mode holds indefinitely.
                </div>
              )}

              {/* Feb 21, 2026 (late eve) — Recent Express payouts.
                  Mirror of the SuperAdmin platform-payouts widget but
                  scoped to the creator's own Express account. Helps them
                  verify "did $X actually hit my bank?" without leaving. */}
              {stripePayouts && stripePayouts.length > 0 && (
                <div className="mt-3 pt-3 border-t hairline max-w-xl" data-testid="earnings-recent-payouts">
                  <div className="text-[10px] uppercase tracking-[0.18em] text-[#6B6A68] mb-1.5">
                    Recent payouts to your bank
                  </div>
                  <div className="space-y-1">
                    {stripePayouts.map((p) => (
                      <ExpressPayoutRow key={p.id} p={p} />
                    ))}
                  </div>
                </div>
              )}
              {stripePayouts && stripePayouts.length === 0 && stripeBalance?.connected && (
                <div className="mt-3 pt-3 border-t hairline max-w-xl text-[10px] text-[#6B6A68] italic" data-testid="earnings-no-payouts">
                  No payouts to your bank yet. Once Stripe sweeps your available balance, the last 5 will show here.
                </div>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {!stripeCfg ? (
              <button disabled data-testid="earnings-cashout-btn-loading"
                className="text-xs px-3 py-2 rounded-md bg-[#6B6A68]/30 text-[#6B6A68] cursor-not-allowed">
                Loading…
              </button>
            ) : !stripeCfg.stripe_account_id ? (
              <button
                onClick={startStripeOnboarding}
                disabled={connectBusy}
                data-testid="earnings-cashout-connect-btn"
                className="text-xs px-3 py-2 rounded-md bg-[#4A5D4E] text-[#FDFCF8] hover:bg-[#3A4A3E] flex items-center gap-1.5 disabled:opacity-50"
              >
                <Bank size={12} weight="bold" /> {connectBusy ? "Opening Stripe…" : "Connect bank"}
              </button>
            ) : !stripeCfg.payouts_enabled ? (
              <button
                onClick={startStripeOnboarding}
                disabled={connectBusy}
                data-testid="earnings-cashout-resume-btn"
                className="text-xs px-3 py-2 rounded-md bg-[#C16E5A] text-[#FDFCF8] hover:bg-[#A85540] flex items-center gap-1.5 disabled:opacity-50"
              >
                <Bank size={12} weight="bold" /> {connectBusy ? "Opening Stripe…" : "Finish bank setup"}
              </button>
            ) : (
              <button
                onClick={() => setCashoutOpen(true)}
                data-testid="earnings-withdraw-btn"
                title="Withdraw earnings via Stripe Express"
                className="text-xs px-4 py-2 rounded-md bg-[#1C1B1A] text-[#FDFCF8] hover:bg-[#3A4A3E] flex items-center gap-1.5"
              >
                <Bank size={12} weight="bold" /> Withdraw earnings
              </button>
            )}
          </div>
        </div>

        {/* Pending affiliate earnings — Feb 19, 2026. Surfaces what's about
            to land in Stripe so the user knows the sweep cron is working. */}
        {(data.pending_affiliate_payout_cents || 0) > 0 && (
          <div className="mt-4 pt-4 border-t hairline" data-testid="earnings-pending-affiliate-row">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-md bg-white border hairline flex items-center justify-center shrink-0">
                <Crown size={18} weight="duotone" className="text-[#C16E5A]" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-[#1C1B1A] flex items-center gap-2 flex-wrap">
                  Pending Partner earnings
                  <span className="font-mono text-[#C16E5A] tabular-nums">
                    {fmt$(data.pending_affiliate_payout_cents)}
                  </span>
                  <span className="text-[10px] text-[#6B6A68]">
                    ({data.pending_affiliate_payout_count} row{data.pending_affiliate_payout_count === 1 ? "" : "s"})
                  </span>
                </div>
                <div className="text-xs text-[#6B6A68] max-w-xl">
                  Your {basePct}/{perfPct} rev-share earnings. Auto-transfers to your Stripe Express on the hourly sweep
                  (or when your total crosses the $5 minimum). You don't need to do anything — just check your Stripe dashboard.
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Feb 28, 2026 — Pending BYOK voucher sweep. Fires when a creator
            added a BYOK key BEFORE connecting Stripe Express, so the
            stranded voucher couldn't auto-transfer. The cents auto-flush
            the moment Stripe Connect goes online. */}
        {(data.pending_voucher_sweep_cents || 0) > 0 && (
          <div className="mt-4 pt-4 border-t hairline" data-testid="earnings-pending-voucher-sweep-row">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-md bg-[#FDF1ED] border border-[#E5BBA8] flex items-center justify-center shrink-0">
                <Crown size={18} weight="duotone" className="text-[#C16E5A]" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-[#1C1B1A] flex items-center gap-2 flex-wrap">
                  Held for you · waiting on Stripe Express
                  <span className="font-mono text-[#C16E5A] tabular-nums">
                    {fmt$(data.pending_voucher_sweep_cents)}
                  </span>
                </div>
                <div className="text-xs text-[#6B6A68] max-w-xl">
                  Stranded voucher money from when you added a BYOK key. It transfers to your Stripe Express balance the
                  moment Stripe Connect onboarding finishes — no action needed from you here.
                </div>
              </div>
            </div>
          </div>
        )}
      </section>

      {/* ============================================================
          SECTION 2 — AFFILIATE PAYOUTS (was section 3)
          MOVED TO TOP (Feb 21, 2026) — affiliate badge + retro earnings
          are the most exciting / actionable thing on this page. Operator
          OS / tax forms got demoted to the bottom of the dashboard.
          Non-affiliates: a single upsell card via <AffiliateUpgrade />.
          Affiliates: full payouts ledger, DFY queue, ghost report,
                      referral activity, "earned this month" card.
          ============================================================ */}
      {!me?.is_affiliate ? (
        <section className="mb-12" data-testid="earnings-affiliate-section">
          <AffiliateUpgrade />
        </section>
      ) : (
        <section className="mb-12" data-testid="earnings-affiliate-section">
          {/* Iter160m (Randy pt-siloart-loosetext-sweep) — border-b uses
              inline color instead of `.hairline` so this container
              isn't flagged as a "feature card" by the siloart-copy
              exclusion rule. The <h3> heading + red subtitle below are
              LOOSE COPY sitting directly on the content canvas and must
              inherit Silo Art V2 whitetext/blacktext settings. Same
              pattern as the YTRE tab header fix (iter160k). */}
          <div className="flex items-baseline gap-3 mb-5 pb-2 border-b border-[#E5E5E0]">
            <h3 className="font-serif-display text-xl text-[#1C1B1A]">Partner payouts</h3>
            <span className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#B4241A] silo-loose">from creators you've referred</span>
          </div>

          {/* Success badge — AffiliateUpgrade renders its "since X" pill for affiliates. */}
          <section className="mb-6" data-testid="earnings-affiliate-badge-section">
            <AffiliateUpgrade />
          </section>

          {/* "What you earned this month" card (self-gates to affiliates). */}
          <EarnedThisMonthCard />

          {/* Live affiliate earnings ledger. */}
          <section className="mb-6" data-testid="earnings-affiliate-payouts-section">
            <AffiliatePayoutsLedger />
          </section>

          {/* Done-for-You operator surface. */}
          <section className="mb-6 space-y-3" data-testid="earnings-dfy-operator-section">
            <OperatorOptInToggle initialEnabled={!!me?.dfy_operator_enabled} />
            <OperatorDfyQueue />
          </section>

          {/* Ghost Report — affiliate momentum widget. */}
          <section className="mb-8" data-testid="earnings-ghost-section">
            <GhostReport shareUrl={inviteUrl} />
          </section>

          {/* Referral activity — slimmed */}
          <section className="mb-8 bg-white border hairline rounded-xl p-6" data-testid="earnings-referral-section">
            <h3 className="font-serif-display text-xl text-[#1C1B1A] mb-3 flex items-center gap-2">
              <Crown size={18} weight="duotone" className="text-[#1C1B1A]" /> Referral activity
              <InfoTip content={`Every signup that flows through your invite link. As a My Silo Partner you may earn a ${basePct} commission on the subscription of paying creators you refer (${perfPct} at the Performance tier). Commissions are paid only on successful software sales; results vary.`} />
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
              <Stat label="Locked-in" value={data.referrals?.awarded_count || 0} accent />
              <Stat label="Awaiting settle" value={data.referrals?.awaiting_settle_count || 0} />
              <Stat label="Awaiting signup" value={data.referrals?.awaiting_signup_count || 0} />
              <Stat label="Parked" value={data.referrals?.parked_count || 0} />
            </div>
            {canShare ? (
              <div className="bg-[#FDFCF8] border hairline rounded-md p-3 mb-3">
                <div className="text-[10px] uppercase tracking-[0.2em] text-[#6B6A68] mb-1">Your personal invite link</div>
                <div className="text-[11px] font-mono text-[#1C1B1A] break-all select-all" data-testid="earnings-invite-url">{inviteUrl}</div>
                <div className="text-[10px] text-[#6B6A68] mt-1">Anchored to <strong>{primarySilo.name}</strong></div>
              </div>
            ) : (
              <div className="bg-[#FDF1ED] border border-[#E5BBA8] rounded-md p-3 mb-3" data-testid="earnings-no-silos-notice">
                <div className="text-xs text-[#7A3A2A]">
                  Build your first silo to unlock invite sharing. Every link anchors to one of your silos so anyone who taps it has somewhere to land.
                </div>
              </div>
            )}
            <div className="flex gap-2 flex-wrap">
              <button onClick={copyInvite} disabled={!canShare} data-testid="earnings-invite-copy"
                title={canShare ? "Copy your invite link" : "Build your first silo to start sharing"}
                className="px-3 py-2 text-xs rounded-md bg-[#1C1B1A] text-[#FDFCF8] hover:bg-[#3A4A3E] flex items-center gap-1.5 disabled:opacity-40 disabled:cursor-not-allowed">
                <Copy size={12} /> Copy invite
              </button>
              <Link to="/dashboard?tab=users" onClick={() => sessionStorage.setItem("dashboard_tab", "users")} className="text-xs text-[#4A5D4E] hover:text-[#1C1B1A] underline self-center ml-auto"
                data-testid="earnings-link-to-users">
                See your referral base →
              </Link>
            </div>
          </section>
        </section>
      )}

      {/* ============================================================
          SECTION 4 — SILO EARNINGS (your own silo revenue, detail)
          ============================================================ */}
      <section className="mb-12" data-testid="earnings-silo-section">
        {/* Iter160m (Randy pt-siloart-loosetext-sweep) — border-b uses
            inline color instead of `.hairline` so this container isn't
            flagged as a "feature card" by the siloart-copy exclusion
            rule. The <h3> heading + subtitle inherit whitetext. */}
        <div className="flex items-baseline gap-3 mb-5 pb-2 border-b border-[#E5E5E0]">
          <h3 className="font-serif-display text-xl text-[#1C1B1A]">Silo earnings</h3>
          <span className="text-[10px] uppercase tracking-[0.22em] text-[#6B6A68] silo-loose">from your own silos</span>
        </div>

        {/* Per-silo breakdown */}
        <section className="mb-6" data-testid="earnings-per-silo-section">
          {(data.per_silo || []).length === 0 ? (
            <div className="bg-white border-2 border-dashed hairline rounded-xl p-8 text-center text-sm text-[#6B6A68]">
              No silos yet — create one to start earning.
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {data.per_silo.map((s) => (
                <div key={s.silo_id} className="bg-white border hairline rounded-xl p-5" data-testid={`per-silo-${s.silo_slug || s.silo_id}`}>
                  <div className="flex items-start justify-between mb-2">
                    <Link to={`/dashboard/silo/${s.silo_id}`} className="font-medium text-[#1C1B1A] hover:text-[#4A5D4E] truncate">{s.silo_name}</Link>
                    <span className="text-[10px] uppercase tracking-[0.2em] text-[#6B6A68] tabular-nums shrink-0 ml-2">
                      {s.price_cents > 0 ? `${fmt$(s.price_cents)}/mo` : "Free"}
                    </span>
                  </div>
                  <div className="grid grid-cols-3 gap-3 mt-3 text-xs">
                    <Stat label="Paying" value={s.paying_subs} />
                    <Stat label="Free" value={s.free_subs} />
                    <Stat label="MRR" value={fmt$(s.mrr_cents)} accent />
                  </div>
                  <div className="grid grid-cols-2 gap-3 mt-3 pt-3 border-t hairline text-xs">
                    <Stat label="Tips · 30d" value={fmt$(s.tips_30d_cents)} />
                    <Stat label="Tips lifetime" value={fmt$(s.tips_lifetime_cents)} />
                  </div>
                  {/* Feb 21, 2026 — lifetime fan-subscription revenue.
                      Survives price changes — historical Stripe charges
                      are ledger-permanent. Sep 25, 2026 (F2) — the three
                      numbers that tie: supporters paid − platform fee =
                      what you kept. Under the Supporter Service Fee the
                      fee is paid by the supporter, so "yours" equals your
                      price; legacy charges still show the old 5% deduction. */}
                  {(s.sub_lifetime_cents > 0 || s.sub_payment_count > 0) && (
                    <div className="grid grid-cols-3 gap-3 mt-3 pt-3 border-t hairline text-xs"
                      data-testid={`per-silo-subhistory-${s.silo_slug || s.silo_id}`}>
                      <Stat label="Supporters paid" value={fmt$(s.sub_supporter_paid_cents ?? s.sub_lifetime_cents)} />
                      <Stat label="Platform fee" value={fmt$(s.sub_platform_fee_cents ?? 0)} />
                      <Stat label="Yours · subs" value={fmt$(s.sub_lifetime_net_cents)} accent />
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Sparkline — last 6 months. Feb 20, 2026: empty-state added so
            new users don't stare at a blank chart. */}
        <section className="mb-6 bg-white border hairline rounded-xl p-6" data-testid="earnings-chart-section">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-serif-display text-xl text-[#1C1B1A]">Last 6 months</h3>
            <div className="flex gap-1.5 text-xs">
              {[
                { k: "total", label: "Total" }, { k: "tips", label: "Tips" }, { k: "subs", label: "Subs" },
              ].map((b) => (
                <button
                  key={b.k} onClick={() => setChartView(b.k)}
                  data-testid={`chart-toggle-${b.k}`}
                  className={`px-2.5 py-1 rounded-md transition-colors ${
                    chartView === b.k
                      ? "bg-[#1C1B1A] text-white"
                      : "bg-[#F0F1EE] text-[#6B6A68] hover:text-[#1C1B1A]"
                  }`}
                >
                  {b.label}
                </button>
              ))}
            </div>
          </div>
          {max <= 1 ? (
            <div className="h-32 flex flex-col items-center justify-center text-center px-6 border border-dashed hairline rounded-md bg-[#FDFCF8]" data-testid="earnings-sparkline-empty">
              <TrendUp size={22} weight="duotone" className="text-[#4A5D4E] mb-2" />
              <div className="text-sm text-[#1C1B1A] font-medium">Start earning to see your trend line</div>
              <div className="text-[11px] text-[#6B6A68] mt-1 max-w-md">
                Once tips or subscriptions come in, the last 6 months light up here.
              </div>
            </div>
          ) : (
            <div className="flex items-end gap-2 h-32 px-2" data-testid="earnings-sparkline">
              {series.map((m) => {
                const v = m[`${chartView}_cents`] || 0;
                const h = (v / max) * 100;
                return (
                  <div key={m.month} className="flex-1 flex flex-col items-center gap-1.5">
                    <div
                      className="w-full bg-gradient-to-t from-[#4A5D4E] to-[#C16E5A] rounded-t transition-all duration-500 hover:opacity-80"
                      style={{ height: `${Math.max(2, h)}%` }}
                      title={`${m.label}: ${fmt$(v)}`}
                      data-testid={`sparkline-bar-${m.month}`}
                    />
                    <span className="text-[10px] text-[#6B6A68] tabular-nums">{m.label}</span>
                    <span className="text-[10px] text-[#1C1B1A] tabular-nums font-medium">{fmt$(v)}</span>
                  </div>
                );
              })}
            </div>
          )}
        </section>

        {/* TipsModule — own internal heading, no wrapper. */}
        <TipsModule />
      </section>

      {/* ============================================================
          SECTION 6 — OPERATOR OS (Churn / Retention / Tax)
          MOVED TO BOTTOM Feb 21, 2026 — these are admin housekeeping
          surfaces (tax forms, churn metrics, exit policy). Useful but
          not what creators want to see first thing.
          ============================================================ */}
      <OperatorOSPanel />

      <WithdrawModal
        open={cashoutOpen}
        onClose={() => setCashoutOpen(false)}
        onOpenTopup={() => setVoucherTopupOpen(true)}
      />
      <VoucherTopupModal
        open={voucherTopupOpen}
        onClose={() => setVoucherTopupOpen(false)}
        onSuccess={() => reload()}
      />
    </div>
  );
}


// Feb 21, 2026 (late eve) — single Express payout row. Compact horizontal
// layout: status pill | $amount | created/arrived date | bank id. Mirrors
// the SuperAdmin platform payout row but on a light background.
function ExpressPayoutRow({ p }) {
  const statusColor = {
    paid:       "bg-[#2F6B3A] text-white",
    in_transit: "bg-[#8A5A00] text-white",
    pending:    "bg-[#6B5A2D] text-white",
    failed:     "bg-[#7A3A2A] text-white",
    canceled:   "bg-[#6B6A68] text-white",
  }[p.status] || "bg-[#F0F1EE] text-[#6B6A68]";
  const created = p.created ? new Date(p.created * 1000) : null;
  const arrival = p.arrival_date ? new Date(p.arrival_date * 1000) : null;
  const fmt = (cents) => `$${((cents || 0) / 100).toFixed(2)}`;
  return (
    <div className="flex items-center gap-2 text-[11px]" data-testid={`express-payout-${p.id}`}>
      <span className={`text-[9px] uppercase tracking-[0.15em] px-1.5 py-0.5 rounded ${statusColor}`}>
        {p.status}
      </span>
      <span className="font-mono tabular-nums text-[#1C1B1A]">{fmt(p.amount_cents)}</span>
      <span className="text-[#6B6A68]">
        {created ? created.toLocaleDateString(undefined, { month: "short", day: "numeric" }) : "—"}
        {arrival && p.status === "paid" && (
          <> · arrived {arrival.toLocaleDateString(undefined, { month: "short", day: "numeric" })}</>
        )}
      </span>
      <span className="flex-1" />
      <span className="text-[#6B6A68] font-mono text-[10px] truncate max-w-[120px]" title={p.destination}>
        → {p.destination}
      </span>
    </div>
  );
}


function Stat({ label, value, accent = false }) {
  return (
    <div>
      <div className="text-[9px] uppercase tracking-[0.2em] text-[#6B6A68]">{label}</div>
      <div className={`tabular-nums mt-1 ${accent ? "text-[#4A5D4E] font-medium" : "text-[#1C1B1A]"}`}>{value}</div>
    </div>
  );
}
